<template>
    <div>
        <q-dialog :model-value="show" @hide="cancel">
            <q-card class="q-pa-md merge-dialog">
                <q-form @submit="submit" ref="form">
                    <div class="text-h6 q-mb-sm">
                        {{ $t('Merge selected phases') }}
                    </div>
                    <q-banner
                        v-if="showNoProductWarning"
                        inline-actions
                        rounded
                        class="bg-orange q-mb-md text-white"
                    >
                        <template v-slot:avatar>
                            <q-icon
                                name="fal fa-exclamation-triangle"
                                color="white"
                            />
                        </template>
                        {{
                            $t(
                                'Selected phases do not have an assigned product and therefore cannot be merged. Please assign a product to the phases and try again.'
                            )
                        }}
                    </q-banner>
                    <q-select
                        v-model="form.status"
                        :label="$t('Status')"
                        :options="statusOptions"
                        :rules="formRules.status"
                        :disable="isLoading || ongoingPhase !== null"
                        class="q-mb-sm full-width"
                        outlined
                        lazy-rules
                        dense
                        color="accent"
                    >
                        <!-- Option style -->
                        <template v-slot:option="scope">
                            <q-item
                                v-bind="scope.itemProps"
                                v-on="scope.itemEvents"
                            >
                                <q-item-section class="row">
                                    <div>
                                        <q-icon
                                            :color="scope.opt.color"
                                            name="fas fa-circle"
                                            class="q-mr-sm"
                                        ></q-icon>
                                        {{ $t(scope.opt.label) }}
                                    </div>
                                </q-item-section>
                            </q-item>
                        </template>
                        <!-- Selected item style -->
                        <template v-slot:selected>
                            <q-icon
                                :color="form.status['color']"
                                style="margin-bottom: 2px"
                                name="fas fa-circle"
                            ></q-icon>
                            <div style="margin-left: 5px">
                                {{ $t(form.status['label']) }}
                            </div>
                        </template>
                    </q-select>
                    <q-select
                        v-if="showCauseDropdown && causeOptions.length > 0"
                        v-model="form.cause"
                        :label="$t('Cause')"
                        :rules="formRules.cause"
                        :options="causeOptions"
                        :disable="isLoading || hasRunningPhase"
                        dense
                        outlined
                        clearable
                        color="accent"
                    >
                    </q-select>
                    <q-select
                        v-if="showProductDropdown"
                        v-model="form.product"
                        :label="$t('Product')"
                        :rules="formRules.product"
                        :options="productOptions"
                        :disable="isLoading || ongoingPhase !== null"
                        class="q-mb-sm"
                        dense
                        outlined
                        clearable
                        color="accent"
                    >
                    </q-select>
                    <q-input
                        data-test="description-textarea"
                        v-model="form.description"
                        :label="$t('Description')"
                        :rules="formRules.description"
                        :input-style="'min-height: 45px; max-height: 90px;'"
                        :disable="isLoading"
                        type="textarea"
                        class="q-mb-md"
                        outlined
                        dense
                        color="accent"
                    ></q-input>
                    <banner
                        class="q-mb-md"
                        :is-visible="ongoingPhase !== null"
                        type="info"
                        :message="$t(editPhaseInfoMessage)"
                    />
                    <div class="form__actions">
                        <q-btn
                            id="merge-phases-submit-button"
                            :loading="isLoading"
                            :label="$t('Submit')"
                            class="float-right q-mb-sm"
                            color="primary"
                            type="submit"
                        />
                        <q-btn
                            @click="cancel"
                            :disable="isLoading"
                            :label="$t('Cancel')"
                            class="float-right q-mb-sm q-mr-sm"
                            text-color="primary"
                            color="white"
                            outline
                            v-close-popup
                        ></q-btn>
                    </div>
                </q-form>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import {
    ASSET_STATUS_TO_STYLE,
    STATUS_OPTIONS,
    getStatusOptionProperty
} from '@models/assets.js';
import AsyncManager from '@utils/asyncManager.js';
import { mapActions, mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import Banner from '@components/Banner.vue';
import { editPhaseInfoMessage } from '@utils/getMessages.js';

export default {
    name: 'MergePhaseDialog',
    components: {
        Banner
    },
    emits: ['merge', 'refresh', 'close'],
    props: {
        assetId: {
            type: [Number, String],
            required: false
        },
        phases: {
            type: [Array],
            required: true
        },
        show: {
            type: Boolean,
            required: true
        }
    },
    created() {
        // initialize async manager instance
        this.asyncManager = new AsyncManager({});
        // initialize options
        this.form.status = this.statusOptions[0];
        this.form.description = this.concatDescriptions();
    },
    beforeUnmount() {
        // let the celery task continue executing but stop periodic status check
        try {
            this.asyncManager.stopPeriodicStatusCheck();
        } catch (error) {
            console.error(error.message);
        }
    },
    watch: {
        'form.status.value': {
            handler() {
                // initialize options selected on status change
                const status = this.form.status.value;
                if (status === 'running') {
                    if (this.productOptions.length > 0) {
                        this.form.product = this.productOptions[0];
                    }
                }

                if (status === 'changeover' || status === 'in_downtime') {
                    if (this.causeOptions.length > 0) {
                        this.form.cause = this.causeOptions[0];
                    }
                    if (this.productOptions.length > 0) {
                        this.form.product = this.productOptions[0];
                    }
                }
            }
        }
    },
    data() {
        return {
            ASSET_STATUS_TO_STYLE,
            form: {
                cause: null,
                product: null,
                status: null,
                description: ''
            },
            formRules: {
                product: [
                    val =>
                        this.validateProduct(val) || this.$t('Select a product')
                ],
                cause: [
                    val => this.validateCause(val) || this.$t('Select a cause')
                ],
                status: [val => val || this.$t('Select a status')]
            },
            isLoading: false,
            editPhaseInfoMessage
        };
    },
    computed: {
        ...mapState(useAssetsStore, ['getAssetPhase']),
        /**
         * Function to return ongoing phase if any.
         * @return {Object}
         */
        ongoingPhase() {
            return this.phases.find(phase => phase.end === null) || null;
        },
        /**
         * Check if one of the phases is a running phase.
         * @return {Boolean} - true if one of the phases is running
         */
        hasRunningPhase() {
            return this.phases.some(phase => phase.phase === 'running');
        },
        /**
         * Payload to send to server.
         * @return {Object}
         */
        payload() {
            const status = this.form.status ? this.form.status.value : null;
            const payload = {
                phases: this.phases.map(phase => phase.id),
                phase: status,
                cause: null,
                product: null,
                description: this.form.description,
                asynchronous: true
            };

            if (status === 'running') {
                payload['product'] = this.form.product
                    ? this.form.product.value
                    : null;
            }

            if (status === 'changeover' || status === 'in_downtime') {
                payload['cause'] = this.form.cause
                    ? this.form.cause.value
                    : null;
                payload['product'] = this.form.product
                    ? this.form.product.value
                    : null;
            }

            return payload;
        },
        /**
         * Condition to show cause dropdown.
         */
        showCauseDropdown() {
            if (this.form.status === null) {
                return false;
            } else {
                // show cause dropdown if status is changeover or in_downtime
                if (
                    this.form.status.value === 'changeover' ||
                    this.form.status.value === 'in_downtime'
                ) {
                    return true;
                }
                return false;
            }
        },
        /**
         * Condition to show product dropdown.
         */
        showProductDropdown() {
            if (this.form.status === null) {
                return false;
            } else {
                return this.form.status.value !== 'stopped';
            }
        },
        /**
         * Selectable cause options.
         * @return {Object[]}
         */
        causeOptions() {
            const causeOptions = [];
            // get distinct cause ids from the phases to be merged
            const seenCauseIds = new Set();
            // build the selectable causes in q-select format
            for (const phase of this.phases) {
                const causeId = phase.cause;
                if (causeId && !seenCauseIds.has(causeId)) {
                    causeOptions.push({
                        label: phase.cause_name,
                        value: causeId
                    });
                    seenCauseIds.add(causeId);
                }
            }
            return causeOptions;
        },
        /**
         * Selectable product options.
         * @return {Object[]}
         */
        productOptions() {
            const productOptions = [];
            if (this.ongoingPhase !== null) {
                productOptions.push({
                    label: this.ongoingPhase.product_name,
                    value: this.ongoingPhase.product
                });
            } else {
                // get distinct product ids from the phases to be merged
                const seenProductIds = new Set();
                // build the selectable products in q-select format
                for (const phase of this.phases) {
                    const productId = phase.product;
                    if (productId && !seenProductIds.has(productId)) {
                        productOptions.push({
                            label: phase.product_name,
                            value: productId
                        });
                        seenProductIds.add(productId);
                    }
                }
            }

            return productOptions;
        },
        /**
         * Selectable status options.
         * @return {Object[]}
         */
        statusOptions() {
            // build the selectable phases in q-select format
            const statusOptions = [];
            if (this.ongoingPhase !== null) {
                statusOptions.push({
                    color: getStatusOptionProperty(
                        this.ongoingPhase.phase,
                        'color'
                    ),
                    label: getStatusOptionProperty(
                        this.ongoingPhase.phase,
                        'label'
                    ),
                    value: this.ongoingPhase.phase
                });
            } else {
                // get distinct statuses from the phases to be merged
                const statuses = new Set(this.phases.map(phase => phase.phase));
                for (const status of statuses) {
                    statusOptions.push({
                        color: getStatusOptionProperty(status, 'color'),
                        label: getStatusOptionProperty(status, 'label'),
                        value: status
                    });
                }
            }

            return statusOptions;
        },
        /**
         * Condition to show warning if no product exists when trying
         * to merge phases that are not "stopped"
         * Stopped phases do not contain Products.
         * If Status is Stopped, Phases can be merged without products
         * Any other status('running', 'changeover, 'asset down') requires at least one product to merge phases,
         */
        showNoProductWarning() {
            if (this.showProductDropdown && this.productOptions.length === 0) {
                return true;
            }
            return false;
        }
    },
    methods: {
        ...mapActions(useAssetsStore, ['loadCurrentPhasesAction']),
        /**
         * Process the merge request.
         */
        async submit() {
            this.isLoading = true;
            // check for merging ongoing phases
            if (this.ongoingPhase !== null) {
                try {
                    await this.loadCurrentPhasesAction({
                        assetId: this.assetId
                    });
                    // get the most recent phase as recorded in the store
                    const mostRecentPhase = this.getAssetPhase(this.assetId);

                    if (!mostRecentPhase) {
                        throw new Error(
                            this.$t(
                                'Failed to validate ongoing phase. Please refresh the page and try again.'
                            )
                        );
                    }
                    // check if the ongoing phase is the most recent phase, if not, show error
                    if (
                        this.ongoingPhase.uuid !== mostRecentPhase.phaseUUID ||
                        this.ongoingPhase.start !== mostRecentPhase.start ||
                        this.ongoingPhase.phase !== mostRecentPhase.status ||
                        this.ongoingPhase.product !==
                            mostRecentPhase.currentProduct
                    ) {
                        // the ongoing phase is not the most recent phase
                        throw new Error(
                            this.$t(
                                'A phase you are trying to merge has been updated. Please refresh the page and try again.'
                            )
                        );
                    }
                } catch (error) {
                    this.$notifyError(error?.message || error);
                    this.isLoading = false;
                    return;
                }
            }

            // validate form (note: manually call validate, instead of using type="submit" on the q-btn element, sometimes forms with one input behave strangely and refreshes the page on submit)
            await this.$refs.form.validate().then(async success => {
                try {
                    const result = await this.asyncManager.asyncLoad(
                        'assetphases/merge/',
                        this.payload
                    );
                    if ('error' in result[0]) {
                        // merge had an error during execution
                        const error = result[0]['error'];
                        if (error === 'unprocessed_phases') {
                            this.$notifyError(
                                this.$t(
                                    'Data for these phases are still processing. Please try again later.'
                                )
                            );
                        } else if (error === 'phase_existence') {
                            this.$notifyError(
                                this.$t(
                                    'Failed to merge. One of the phases no longer exists. Please refresh the page and try again.'
                                )
                            );
                        } else if (error === 'latest_phase') {
                            this.$notifyError(
                                this.$t(
                                    'Failed to merge. The selected phase is no longer the latest phase.'
                                )
                            );
                        } else if (error === 'cause_invaild') {
                            this.$notifyError(
                                this.$t(
                                    'Failed to merge. This cause is no longer linked to this asset.'
                                )
                            );
                        } else if (error === 'product_invalid') {
                            this.$notifyError(
                                this.$t(
                                    'Failed to merge. This product is no longer linked to this asset.'
                                )
                            );
                        } else if (error === 'ongoing_details_edit') {
                            this.$notifyError(
                                this.$t(
                                    'The status, product, and cause of an ongoing phase cannot be edited while merging.'
                                )
                            );
                        } else if (error === 'different_assets') {
                            this.$notifyError(
                                this.$t(
                                    'Phases to be merged must be part of the same asset.'
                                )
                            );
                        } else if (error === 'at_least_two') {
                            this.$notifyError(
                                this.$t(
                                    'Must select at least two phases to merge.'
                                )
                            );
                        } else if (error == 'uneditable_phase') {
                            this.$notifyError(
                                this.$t(
                                    "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                                    {
                                        editablePeriod: parseInt(
                                            process.env
                                                .EDITABLE_PHASE_DURATION_DAYS ||
                                                5 // 5 days
                                        )
                                    }
                                )
                            );
                        } else {
                            this.$notifyError(
                                this.$t(
                                    'An unexpected error occurred - Failed to merge phases. Please try again later.'
                                ),
                                error
                            );
                        }
                    } else {
                        // merge was successful
                        this.$notifySuccess(this.$t('Phases merged.'));
                        this.$emit('merge');
                        this.cancel();
                    }
                } catch (e) {
                    const error =
                        e?.response?.data && typeof e.response.data === 'object'
                            ? e.response.data
                            : {};
                    try {
                        if (
                            error &&
                            typeof error == 'object' &&
                            'phases' in error &&
                            error['phases'][0].includes('Invalid pk')
                        ) {
                            // it's possible that another users may be merging from an outdated phase list, so some phases no longer actually exist in the database
                            this.$notifyError(
                                this.$t(
                                    'Failed to merge. One of the phases no longer exists. Please refresh the page and try again.'
                                )
                            );
                            // if the phase list is outdated, close the popup to lead the user to refresh the page
                            this.cancel();
                            this.$emit('refresh');
                        } else if (
                            error &&
                            typeof error == 'object' &&
                            'uneditable_phase' in error
                        ) {
                            this.$notifyError(
                                this.$t(
                                    "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                                    {
                                        editablePeriod: parseInt(
                                            process.env
                                                .EDITABLE_PHASE_DURATION_DAYS ||
                                                5 // 5 days
                                        )
                                    }
                                )
                            );
                        } else if (error && typeof error == 'object') {
                            // get the name of the field containing errors
                            const errorKey = Object.keys(error)[0];
                            // show backend API message
                            this.$notifyError(
                                this.$t('Failed to merge phases due to:') +
                                    ` ${error[errorKey]}`
                            );
                        } else {
                            this.$notifyError(
                                this.$t('Failed to merge phases due to:') +
                                    ` ${error}`
                            );
                        }
                    } catch {
                        // show standard error message
                        this.$notifyError(
                            this.$t(
                                'An unexpected error occurred - Failed to merge phases. Please try again later.'
                            ),
                            error
                        );
                    }
                }
            });
            this.isLoading = false;
        },
        /**
         * Close the dialog.
         */
        cancel() {
            this.$emit('close');
        },
        /**
         * Validate cause field.
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateCause(cause) {
            // check whether cause field needs to be validated
            if (this.form.status.value === 'changeover') {
                return cause !== null;
            } else {
                return true;
            }
        },
        /**
         * Validate product field.
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateProduct(product) {
            // check whether cause field needs to be validated
            if (this.form.status.value !== 'stopped') {
                return product !== null;
            } else {
                return true;
            }
        },
        /**
         * Concatenate descriptions from all the phases to be merged.
         * @return {String}
         */
        concatDescriptions() {
            let descriptions = '';
            for (const phase of this.phases) {
                if (phase.description.length > 0) {
                    descriptions += '\n' + phase.description;
                }
            }
            return descriptions;
        }
    }
};
</script>

<style lang="scss" scoped>
.merge-dialog {
    width: 90%;
}
</style>
